import { ShieldCheck } from "lucide-react";
import { motion, type Variants } from "motion/react";
import type { ComponentType } from "react";

import { ClientAgents } from "./bento/client-agents";
import { MarketDataGlobe } from "./bento/market-data-globe";
import { ResearchLibrary } from "./bento/research-library";
import { SignalMatching } from "./bento/signal-matching";
import { MagicCard } from "@/components/magicui/magic-card";
import { DashboardPreview } from "@/features/landing/modules/dashboard-preview/dashboard-preview";
import { m } from "@/paraglide/messages.js";

const EASE = [0.25, 0.1, 0.25, 1] as const;

const headerVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const gridVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const viewport = { once: true, margin: "-80px" } as const;

type IllustrationKey = "research-library" | "signal-matching" | "client-agents";

const ILLUSTRATIONS: Record<IllustrationKey, ComponentType> = {
  "research-library": ResearchLibrary,
  "signal-matching": SignalMatching,
  "client-agents": ClientAgents,
};

const TOP_FEATURES: { title: string; description: string; illustration: IllustrationKey }[] = [
  {
    title: m.landing_capability_alpha_title(),
    description: m.landing_capability_alpha_description(),
    illustration: "research-library",
  },
  {
    title: m.landing_capability_desk_title(),
    description: m.landing_capability_desk_description(),
    illustration: "signal-matching",
  },
  {
    title: m.landing_capability_buyside_title(),
    description: m.landing_capability_buyside_description(),
    illustration: "client-agents",
  },
];

const cardGradientProps = {
  gradientSize: 240,
  gradientFrom: "var(--primary)",
  gradientTo: "var(--secondary)",
  gradientColor: "color-mix(in oklch, var(--primary) 22%, transparent)",
} as const;

/**
 * Bento capability grid. A row of three equal cards, then a wide "sovereign
 * deployment" card paired with a narrow "live market data" card.
 */
export const FeaturesSection = () => {
  return (
    <section
      id="capabilities"
      data-tour="landing-capabilities"
      className="mx-auto w-full max-w-[1320px] scroll-mt-20 px-5 py-16 md:py-24"
    >
      <motion.div
        className="mx-auto flex max-w-2xl flex-col items-center gap-4 text-center"
        variants={headerVariants}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        <motion.h2 variants={itemVariants} className="text-3xl font-semibold leading-tight text-foreground md:text-5xl">
          {m.landing_capabilities_title()}
        </motion.h2>
        <motion.p variants={itemVariants} className="max-w-xl text-base font-medium text-muted-foreground md:text-lg">
          {m.landing_capabilities_subtitle()}
        </motion.p>
      </motion.div>

      <motion.div
        className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-6"
        variants={gridVariants}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        {/* Top row: three equal cards */}
        {TOP_FEATURES.map((item) => {
          const Illustration = ILLUSTRATIONS[item.illustration];
          return (
            <motion.div key={item.title} variants={cardVariants} className="flex flex-col lg:col-span-2">
              <MagicCard className="flex h-full flex-col rounded-2xl" {...cardGradientProps}>
                <div className="flex flex-col gap-1.5 p-6">
                  <p className="text-lg font-medium leading-7 text-foreground">{item.title}</p>
                  <p className="text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                </div>
                <div className="relative h-64">
                  <Illustration />
                </div>
              </MagicCard>
            </motion.div>
          );
        })}

        {/* Bottom row: wide "sovereign deployment" card (2/3) */}
        <motion.div
          variants={cardVariants}
          className="flex flex-col md:col-span-2 md:order-last lg:order-none lg:col-span-4"
        >
          <MagicCard className="flex h-full flex-col overflow-hidden rounded-2xl" {...cardGradientProps}>
            <div className="relative min-h-[320px] lg:min-h-[380px]">
              {/*
                Same app shell as under the hero, rendered at full design width and
                scaled up so only its top-left quadrant shows, bleeding past the
                card edges (clipped by the card's rounded overflow).
              */}
              <div className="absolute left-1/2 top-12 w-[1160px] origin-top-left scale-[1.1] opacity-50 md:opacity-100">
                <DashboardPreview className="max-w-none px-0" />
              </div>

              {/* Scrim so the text stays legible over the cropped screenshot */}
              <div className="absolute top-0 left-0 w-full h-[calc(100%+100px)] bg-[radial-gradient(125%_130%_at_0%_100%,var(--background)_36%,color-mix(in_oklch,var(--background)_55%,transparent)_52%,transparent_70%)]" />

              <div className="absolute top-1/2 -translate-y-1/2 left-0 flex max-w-sm flex-col gap-3 p-6">
                <div className="flex size-10 items-center justify-center rounded-xl border border-border bg-card text-foreground">
                  <ShieldCheck className="size-5" />
                </div>
                <p className="text-2xl font-semibold leading-tight text-foreground md:text-3xl">
                  {m.landing_capability_sovereign_title()}
                </p>
                <div className="flex flex-col gap-2 text-sm leading-relaxed text-muted-foreground">
                  <p>{m.landing_capability_sovereign_body_1()}</p>
                  <p>{m.landing_capability_sovereign_body_2()}</p>
                </div>
              </div>
            </div>
          </MagicCard>
        </motion.div>

        {/* Bottom row: narrow "live market data" card (1/3) */}
        <motion.div variants={cardVariants} className="flex flex-col lg:col-span-2">
          <MagicCard className="flex h-full flex-col rounded-2xl" {...cardGradientProps}>
            <div className="flex flex-col gap-1.5 p-6">
              <p className="text-lg font-medium leading-7 text-foreground">
                {m.landing_capability_market_data_title()}
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {m.landing_capability_market_data_description()}
              </p>
            </div>
            <div className="relative h-64">
              <MarketDataGlobe />
            </div>
          </MagicCard>
        </motion.div>
      </motion.div>
    </section>
  );
};
